import React, {useState} from "react";
import "./UserCard.css";
import {Accordion, ListGroup, Table} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faCircleNodes,
    faMobileScreenButton,
    faCircle,
    faBatteryFull,
    faBatteryThreeQuarters,
    faBatteryHalf,
    faBatteryQuarter,
    faBatteryEmpty,
    faSort,
    faSortUp,
    faSortDown,
    faCode,
} from "@fortawesome/free-solid-svg-icons";
import {faApple, faAndroid} from "@fortawesome/free-brands-svg-icons";
import Form from "react-bootstrap/Form";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import ReactJson from "react-json-view";
import { useTimezone } from "../../../../contexts/TimezoneContext";
import { formatTargetName } from "../../../../../../lib/ui/badgeUtils";
import { getBondColor } from "../../../../../../lib/ui/colorUtils";
import { getUserActionsHistory, getUserActionsHistoryV2 } from "../../../../libs/dashboard";
import ActionsHistorySection from "./ActionsHistorySection";

// Client badges, name mapping, and the per-source feed accordion live in
// ActionsHistorySection — rendered once per action-history source below.


function UserCard({userData, bondsData = {}, bonds, selectedHomeId = null}) {
    // Use timezone context for all date formatting - must be called before any conditional returns
    const { formatDate, getRelativeTime } = useTimezone();

    // State for sorting bond products table
    const [bondSortColumn, setBondSortColumn] = useState('bond_id');
    const [bondSortDirection, setBondSortDirection] = useState('desc');

    if (!userData) {
        return null;
    }

    // Bonds for the card's bond-scoped sections (Action History filters, Bond Products,
    // Sidekicks). When a home is selected this is that home's bonds; a migrated user's
    // own owner_id bonds are empty, so keying on them used to hide these sections.
    const effectiveBonds = bonds || userData?.["bonds"] || [];

    const getMobileIcon = (manufacturer) => {
        return manufacturer.toLowerCase() === 'apple' ? faApple : faAndroid;
    };

    const getMobileColor = (manufacturer) => {
        return manufacturer.toLowerCase() === 'apple' ? 'primary' : 'success';
    };

    const getBatteryInfo = (battery) => {
        if (battery === undefined) return null;

        let icon, color;
        if (battery > 75) {
            icon = faBatteryFull;
            color = "success";
        } else if (battery > 50) {
            icon = faBatteryThreeQuarters;
            color = "success";
        } else if (battery > 25) {
            icon = faBatteryHalf;
            color = "warning";
        } else if (battery > 10) {
            icon = faBatteryQuarter;
            color = "danger";
        } else {
            icon = faBatteryEmpty;
            color = "danger";
        }

        return { icon, color };
    };

    // Extract all sidekicks from all bonds (excluding Mates)
    const getAllSidekicks = () => {
        const sidekicksList = [];
        if (effectiveBonds) {
            effectiveBonds.forEach(bond => {
                // Skip Mate devices (bonds starting with "SM")
                if (bond.bond_id.startsWith('SM')) {
                    return;
                }

                const bondData = bondsData[bond.bond_id];
                if (bondData?.sidekicks) {
                    const bondSidekicks = Array.isArray(bondData.sidekicks)
                        ? bondData.sidekicks
                        : Object.values(bondData.sidekicks);
                    bondSidekicks.forEach(sidekick => {
                        const sidekickId = sidekick.sidekick_id || sidekick.id;
                        const sidekickData = sidekick.data || {};

                        // Include all sidekicks (both regular "SH" and Breeze Pro "BWS-1000")
                        // Filter out only non-sidekick devices (those that don't start with "SH" and aren't Breeze Pro)
                        const isBreezePro = sidekickData.model === "BWS-1000";
                        const isRegularSidekick = sidekickId && sidekickId.startsWith('SH');

                        if (isBreezePro || isRegularSidekick) {
                            sidekicksList.push({
                                ...sidekick,
                                bond_id: bond.bond_id
                            });
                        }
                    });
                }
            });
        }
        return sidekicksList;
    };

    const allSidekicks = getAllSidekicks();

    // Prepare bond products data for table
    const getBondProductsTableData = () => {
        const products = [];

        // Add bonds
        if (effectiveBonds) {
            effectiveBonds.forEach(bond => {
                const bondData = bondsData[bond.bond_id];
                const sysVersion = bondData?.sys?.version || {};
                const isMate = bond.bond_id.startsWith('SM');
                const firstChar = bond.bond_id.charAt(0);
                const isSmartByBond = firstChar === 'T' || firstChar === 'K' || firstChar === 'H';
                const createdDate = bondData?.created || bond.created;

                // Determine product name
                let productName;
                if (isMate) {
                    productName = "Mate";
                } else if (isSmartByBond) {
                    productName = "Smart by Bond";
                } else {
                    productName = formatTargetName(sysVersion.target) || "Bond";
                }

                products.push({
                    id: bond.bond_id,
                    bond_id: bond.bond_id,
                    product_name: productName,
                    created: createdDate,
                    type: isMate ? 'mate' : isSmartByBond ? 'smart' : 'bond'
                });
            });
        }

        // Add sidekicks
        allSidekicks.forEach(sidekick => {
            const sidekickData = sidekick.data || {};
            const isBreezePro = sidekickData.model === "BWS-1000";
            const sidekickId = sidekick.sidekick_id || sidekick.id;

            products.push({
                id: sidekickId,
                bond_id: sidekickId,
                product_name: isBreezePro ? "Breeze Pro" : "Sidekick",
                created: sidekick.created,
                type: isBreezePro ? 'breeze' : 'sidekick'
            });
        });

        return products;
    };

    // Sort bond products
    const sortBondProducts = (products) => {
        return [...products].sort((a, b) => {
            let aVal = a[bondSortColumn];
            let bVal = b[bondSortColumn];

            // Handle null/undefined values
            if (!aVal && !bVal) return 0;
            if (!aVal) return 1;
            if (!bVal) return -1;

            // Compare based on column type
            if (bondSortColumn === 'created') {
                aVal = new Date(aVal).getTime();
                bVal = new Date(bVal).getTime();
            }

            const comparison = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
            return bondSortDirection === 'asc' ? comparison : -comparison;
        });
    };

    // Handle column header click
    const handleBondSort = (column) => {
        if (bondSortColumn === column) {
            setBondSortDirection(bondSortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setBondSortColumn(column);
            setBondSortDirection('asc');
        }
    };

    // Get sort icon for column
    const getSortIcon = (column) => {
        if (bondSortColumn !== column) return faSort;
        return bondSortDirection === 'asc' ? faSortUp : faSortDown;
    };

    // Get badge color for product type
    const getProductBadgeColor = (type) => {
        switch (type) {
            case 'mate': return 'warning';
            case 'smart': return 'primary';
            case 'breeze': return 'success';
            case 'sidekick': return 'warning';
            default: return 'info';
        }
    };

    const bondProducts = getBondProductsTableData();
    const sortedBondProducts = sortBondProducts(bondProducts);

    return (<Card className="mb-2">
        <Card.Body className={"p-2"}>
            <div className={"p-2"}>
                <FloatingLabel label="Email" className="mb-2">
                    <Form.Control type="email" readOnly={true} value={userData.email}/>
                </FloatingLabel>
                <FloatingLabel label="Name" className="mb-2">
                    <Form.Control readOnly={true} value={userData.name}/>
                </FloatingLabel>

                <div className="d-flex gap-2 mb-2">
                    <FloatingLabel label="ID" className="flex-fill">
                        <Form.Control readOnly={true} value={userData.id}/>
                    </FloatingLabel>
                    <FloatingLabel label="Account Code" className="flex-fill">
                        <Form.Control readOnly={true} value={userData.account_code}/>
                    </FloatingLabel>
                </div>

                <div className="d-flex gap-2 mb-2">
                    <FloatingLabel label={`Joined ${getRelativeTime(userData.created)}`} className="flex-fill">
                        <Form.Control readOnly={true} value={formatDate(userData.created)}/>
                    </FloatingLabel>
                    <FloatingLabel label={`Last Modified ${getRelativeTime(userData.modified)}`} className="flex-fill">
                        <Form.Control readOnly={true} value={formatDate(userData.modified)}/>
                    </FloatingLabel>
                </div>
            </div>
            {effectiveBonds && effectiveBonds.length > 0 && (
                <>
                    <ActionsHistorySection
                        title="Action History"
                        userId={userData.id}
                        bonds={effectiveBonds}
                        bondsData={bondsData}
                        fetchActions={getUserActionsHistory}
                    />
                    <ActionsHistorySection
                        title="Action History (New)"
                        userId={userData.id}
                        bonds={effectiveBonds}
                        bondsData={bondsData}
                        fetchActions={getUserActionsHistoryV2}
                        detailed
                        homeId={selectedHomeId}
                    />
                </>
            )}

            {userData.integrations && userData.integrations.length > 0 && (
                <Accordion defaultActiveKey="" className="mb-2">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>
                            <div className={"d-flex justify-content-between align-items-center w-100"}>
                                <div>
                                    <FontAwesomeIcon icon={faCircleNodes} className={"me-2"}/>
                                    Integrations
                                </div>
                                <div className={"d-flex gap-1"}>
                                    {userData.integrations.slice(0, 2).map((integration, idx) => (
                                        <Badge
                                            key={idx}
                                            bg={integration.is_active === "True" ? "success" : "danger"}
                                        >
                                            {integration.integration_client_id}
                                            {integration.is_active === "True" && " ✓"}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body className={"p-3"}>
                            <ListGroup>
                                {userData.integrations.map((integration, idx) => (
                                    <ListGroup.Item key={idx} className={"d-flex justify-content-between align-items-center"}>
                                        <Badge
                                            bg={integration.is_active === "True" ? "success" : "danger"}
                                        >
                                            {integration.integration_client_id}
                                            {integration.is_active === "True" && " ✓"}
                                        </Badge>
                                        <Badge bg={"secondary"}>
                                            {formatDate(integration.last_login || integration.created)}
                                        </Badge>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            )}

            {bondProducts.length > 0 && (
                <Accordion defaultActiveKey="" className="mb-2">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>
                            <div className={"d-flex justify-content-between align-items-center w-100"}>
                                <div>
                                    <FontAwesomeIcon icon={faCircle} className={"me-2"}/>
                                    Bond Products
                                </div>
                                <Badge bg={"dark"}>
                                    {bondProducts.length}
                                </Badge>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body className={"p-2"}>
                            <Table hover responsive size="sm">
                                <thead>
                                    <tr>
                                        <th
                                            onClick={() => handleBondSort('bond_id')}
                                            style={{cursor: 'pointer', userSelect: 'none'}}
                                        >
                                            Bond ID{' '}
                                            <FontAwesomeIcon icon={getSortIcon('bond_id')} size="sm" />
                                        </th>
                                        <th
                                            onClick={() => handleBondSort('product_name')}
                                            style={{cursor: 'pointer', userSelect: 'none'}}
                                        >
                                            Product Name{' '}
                                            <FontAwesomeIcon icon={getSortIcon('product_name')} size="sm" />
                                        </th>
                                        <th
                                            onClick={() => handleBondSort('created')}
                                            style={{cursor: 'pointer', userSelect: 'none'}}
                                        >
                                            Created{' '}
                                            <FontAwesomeIcon icon={getSortIcon('created')} size="sm" />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedBondProducts.map((product, idx) => (
                                        <tr key={`product-${idx}`}>
                                            <td>
                                                <Badge className="bondBadge" style={{'--bond-badge-bg': getBondColor(product.bond_id)}}>
                                                    {product.bond_id}
                                                </Badge>
                                            </td>
                                            <td>
                                                <Badge bg={getProductBadgeColor(product.type)}>
                                                    {product.product_name}
                                                </Badge>
                                            </td>
                                            <td>
                                                {product.created ? formatDate(product.created) : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            )}

            {userData.mobiles && userData.mobiles.length > 0 && (
                <Accordion defaultActiveKey="" className="mb-2">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>
                            <div className={"d-flex justify-content-between align-items-center w-100"}>
                                <div>
                                    <FontAwesomeIcon icon={faMobileScreenButton} className={"me-2"}/>
                                    Mobile Devices
                                </div>
                                <Badge bg={"dark"}>
                                    {userData.mobiles.length}
                                </Badge>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body className={"p-3"}>
                            <ListGroup>
                                {userData.mobiles.map((mobile, idx) => (
                                    <ListGroup.Item key={idx} className={"d-flex justify-content-between align-items-center"}>
                                        <div>
                                            <Badge
                                                className={"me-2"}
                                                bg={getMobileColor(mobile.manufacturer)}
                                            >
                                                <FontAwesomeIcon icon={getMobileIcon(mobile.manufacturer)} />
                                                {' '}{mobile.manufacturer}
                                            </Badge>
                                            <span>{mobile.model}</span>
                                        </div>
                                        <Badge bg={"secondary"}>
                                            {formatDate(mobile.modified)}
                                        </Badge>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            )}

            {bondsData && Object.keys(bondsData).length > 0 && (
                <Accordion defaultActiveKey="" className="mb-2">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>
                            <div className={"d-flex justify-content-between align-items-center w-100"}>
                                <div>
                                    <FontAwesomeIcon icon={faCode} className={"me-2"}/>
                                    Bond Shadow
                                </div>
                                <Badge bg={"dark"}>
                                    {Object.keys(bondsData).length}
                                </Badge>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body className={"p-3"}>
                            <ReactJson
                                src={bondsData}
                                theme="ocean"
                                collapsed={1}
                                displayDataTypes={false}
                                displayObjectSize={false}
                                enableClipboard={true}
                                name={false}
                            />
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            )}
        </Card.Body>
    </Card>);
}

export default UserCard;
